import { useContext } from 'react'
import { useTranslation } from 'libs/i18n'
import { StoreSettingsContext } from 'store/StoreSettingsContext'
import { ContextMenu, Currency, PayoutDestination } from 'shared/components'
import { Detail } from 'shared/atoms'
import type { Option } from 'shared/components/ContextMenu/types'
import { PayoutStatus } from '../PayoutStatus/PayoutStatus'
import { formatPayoutDate } from '../../utils/payout'
import type { PayoutItem } from '../../types/payouts.types'

export type PayoutProps = {
	payout: PayoutItem
	menuOptions: Option[]
	onOpen: (payout: PayoutItem) => void
}

export const Payout = ({ payout, menuOptions, onOpen }: PayoutProps) => {
	const { t } = useTranslation()
	const settingsCtx = useContext(StoreSettingsContext)
	const isIceland = settingsCtx?.storeSettings?.country === 'IS'
	const {
		id,
		reference,
		created,
		account,
		status,
		total,
		commission,
		takedown_and_storage_fees,
		amount,
		tx_reference,
		payout_method
	} = payout

	return (
		<li className="p-4">
			<div className="flex items-start justify-between gap-3">
				<div className="min-w-0">
					<p className="truncate text-[13px] font-semibold text-foreground">
						{account?.name || '—'}
					</p>
					<p className="truncate text-[11px] text-muted-foreground">{account?.email}</p>
				</div>
				<div className="flex shrink-0 items-center gap-1">
					<PayoutStatus status={status} />
					<ContextMenu
						className="-mr-2"
						dataId={id}
						item={payout}
						options={menuOptions}
						label={t('Actions for payout {{ id }}', { id: reference })}
					/>
				</div>
			</div>
			<dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-[11px]">
				<Detail
					label={t('Payout')}
					value={
						<button
							type="button"
							className="font-semibold text-primary hover:underline"
							onClick={() => onOpen(payout)}>
							#{reference}
						</button>
					}
				/>
				<Detail
					label={t('Created')}
					value={
						<span className="font-normal text-muted-foreground">{formatPayoutDate(created)}</span>
					}
				/>
				<Detail
					label={t('Payout method')}
					value={<PayoutDestination destination={payout_method} />}
				/>
				{isIceland && <Detail label={t('SSN')} value={account?.ssn || '—'} />}
				<Detail label={t('Total')} value={<Currency price={total} />} />
				<Detail label={t('Commission total')} value={<Currency price={commission} />} />
				<Detail label={t('Cost')} value={<Currency price={takedown_and_storage_fees} />} />
				<Detail
					className="col-span-2"
					label={t('Amount')}
					value={<Currency className="font-display text-[15px] font-bold" price={amount} />}
				/>
				{tx_reference && (
					<Detail className="col-span-2" label={t('Transaction Reference')} value={tx_reference} />
				)}
			</dl>
		</li>
	)
}
